import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../stores";
import {
  BillOfMaterials,
  BomItem,
  BomItemFormData,
  BOMFormData,
} from "./types";

class BOMViewStore {
  // Observable state
  currentStep: number = 1;
  editMode: boolean = false;
  selectedProductId: number | null = null;
  selectedBOMId: number | null = null;
  bomData: BOMFormData = {
    bomCode: "",
    name: "",
    description: "",
    version: "1.0",
    effectiveDate: new Date().toISOString().split("T")[0],
    expirationDate: "",
    notes: "",
  };
  bomItems: BomItemFormData[] = [];
  selectedBOMItems: Array<{ itemId: number; quantity: number }> = [];
  selectedMaterials: Array<{ materialId: number; quantity: number }> = [];
  isLoading: boolean = false;
  error: string | null = null;
  activeBOM: BillOfMaterials | null = null;
  savedBOMItems: BomItem[] = [];
  showBOMItemDialog: boolean = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any);
  }

  // Actions
  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  setSelectedProduct(productId: number | null) {
    this.selectedProductId = productId;
    if (productId) {
      const product = this.selectedProduct;
      if (product) {
        // Auto-generate BOM code based on product
        const timestamp = Date.now().toString().slice(-6);
        this.bomData.bomCode = `BOM-${product.name
          ?.replace(/\s+/g, "-")
          .toUpperCase()
          .slice(0, 10)}-${timestamp}`;
        this.bomData.name = `BOM for ${product.name}`;
      }
    }
  }

  updateBOMData(data: Partial<BOMFormData>) {
    this.bomData = { ...this.bomData, ...data };
  }

  addBOMItem(item?: Partial<BomItemFormData>) {
    // Ensure itemType is valid
    let validItemType = "material"; // default
    if (item?.itemType) {
      const itemTypeLower = item.itemType.toLowerCase();
      if (["material", "component", "labor", "service", "tool"].includes(itemTypeLower)) {
        validItemType = itemTypeLower as any;
      }
    }

    const newItem: BomItemFormData = {
      itemType: validItemType as any,
      itemCode: item?.itemCode || "",
      name: item?.name || "",
      description: item?.description || "",
      quantity: item?.quantity || 1,
      unit: item?.unit || "ea",
      unitCost: item?.unitCost || 0,
      vendor: item?.vendor || "",
      partNumber: item?.partNumber || "",
      leadTime: item?.leadTime || 0,
      critical: item?.critical || false,
      alternatives: item?.alternatives || [],
      notes: item?.notes || "",
      sortOrder: item?.sortOrder || this.bomItems.length,
    };
    this.bomItems.push(newItem);
  }

  removeBOMItem(index: number) {
    this.bomItems.splice(index, 1);
    // Update sort order for remaining items
    this.bomItems.forEach((item, idx) => {
      item.sortOrder = idx;
    });
  }

  updateBOMItem(index: number, data: Partial<BomItemFormData>) {
    if (this.bomItems[index]) {
      this.bomItems[index] = { ...this.bomItems[index], ...data };
    }
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  setEditMode(editMode: boolean) {
    this.editMode = editMode;
    // Reset selections when switching modes
    if (editMode) {
      this.selectedProductId = null;
      this.selectedBOMId = null;
      this.bomData = {
        bomCode: "",
        name: "",
        description: "",
        version: "1.0",
        effectiveDate: new Date().toISOString().split("T")[0],
        expirationDate: "",
        notes: "",
      };
    }
  }

  setSelectedBOM(bomId: number | null) {
    this.selectedBOMId = bomId;
  }

  setShowBOMItemDialog(show: boolean) {
    this.showBOMItemDialog = show;
  }

  addSelectedBOMItem(itemId: number, quantity: number) {
    const existingIndex = this.selectedBOMItems.findIndex(
      (item) => item.itemId === itemId
    );
    if (existingIndex >= 0) {
      this.selectedBOMItems[existingIndex].quantity = quantity;
    } else {
      this.selectedBOMItems.push({ itemId, quantity });
    }
  }

  addSelectedMaterial(materialId: number, quantity: number) {
    const existingIndex = this.selectedMaterials.findIndex(
      (item) => item.materialId === materialId
    );
    if (existingIndex >= 0) {
      this.selectedMaterials[existingIndex].quantity = quantity;
    } else {
      this.selectedMaterials.push({ materialId, quantity });
    }
  }

  removeSelectedMaterial(materialId: number) {
    this.selectedMaterials = this.selectedMaterials.filter(
      (item) => item.materialId !== materialId
    );
  }

  updateSelectedMaterialQuantity(materialId: number, quantity: number) {
    const item = this.selectedMaterials.find((item) => item.materialId === materialId);
    if (item) {
      item.quantity = quantity;
    }
  }

  removeSelectedBOMItem(itemId: number) {
    this.selectedBOMItems = this.selectedBOMItems.filter(
      (item) => item.itemId !== itemId
    );
  }

  updateSelectedBOMItemQuantity(itemId: number, quantity: number) {
    const item = this.selectedBOMItems.find((item) => item.itemId === itemId);
    if (item) {
      item.quantity = quantity;
    }
  }

  reset() {
    this.currentStep = 1;
    this.editMode = false;
    this.selectedProductId = null;
    this.selectedBOMId = null;
    this.bomData = {
      bomCode: "",
      name: "",
      description: "",
      version: "1.0",
      effectiveDate: new Date().toISOString().split("T")[0],
      expirationDate: "",
      notes: "",
    };
    this.bomItems = [];
    this.selectedBOMItems = [];
    this.selectedMaterials = [];
    this.isLoading = false;
    this.error = null;
    this.activeBOM = null;
    this.savedBOMItems = [];
    this.showBOMItemDialog = false;
  }

  // Async actions
  async loadExistingBOM() {
    if (!this.selectedBOMId) {
      this.setError("Please select a BOM to edit");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // Get the BOM details
      const bom = this.bomStore.entities.find(
        (b: any) => b.id === this.selectedBOMId || b.billOfMaterialsId === this.selectedBOMId
      );
      
      if (!bom) {
        throw new Error("BOM not found");
      }

      // Load the BOM items for this BOM
      const bomItemsResponse = await (this.rootStore as any).bomItemsStore.find({
        query: {
          bomId: this.selectedBOMId,
          active: true,
        },
      });

      runInAction(() => {
        this.activeBOM = bom;
        this.selectedProductId = bom.productId;
        this.bomData = {
          bomCode: bom.bomCode,
          name: bom.name,
          description: bom.description || "",
          version: bom.version,
          effectiveDate: bom.effectiveDate || new Date().toISOString().split("T")[0],
          expirationDate: bom.expirationDate || "",
          notes: bom.notes || "",
        };
        
        // Store the existing BOM items that are already assigned to this BOM
        if (bomItemsResponse?.data) {
          this.savedBOMItems = bomItemsResponse.data;
          // Clear selectedBOMItems since we're editing existing items
          this.selectedBOMItems = [];
        }
        
        this.isLoading = false;
        // Move to the BOM items step
        this.currentStep = 2;
      });

      return bom;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to load BOM");
        this.isLoading = false;
      });
      throw error;
    }
  }

  async createBOM() {
    if (!this.selectedProductId) {
      this.setError("Please select a product");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // Build payload matching billOfMaterialsData type
      const bomPayload: any = {
        bomCode: this.bomData.bomCode,
        productId: this.selectedProductId,
        name: this.bomData.name,
        description: this.bomData.description || undefined,
        version: this.bomData.version,
        status: "draft",
        effectiveDate: this.bomData.effectiveDate || undefined,
        expirationDate: this.bomData.expirationDate || undefined,
        laborHours: this.bomData.laborHours || undefined,
        notes: this.bomData.notes || undefined,
        createdBy: (this.rootStore as any).userStore?.currentUser?.id || undefined,
        active: true,
      };

      // Remove undefined optional fields
      Object.keys(bomPayload).forEach(key => {
        if (bomPayload[key] === undefined) {
          delete bomPayload[key];
        }
      });

      const createdBOM = await this.bomStore.create(bomPayload);

      runInAction(() => {
        this.activeBOM = createdBOM;
        this.isLoading = false;
      });

      return createdBOM;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to create BOM");
        this.isLoading = false;
      });
      throw error;
    }
  }

  async saveBOMItems() {
    if (!this.activeBOM) {
      this.setError("No active BOM to add items to");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // If we're editing and have existing items, we need to handle updates/deletes
      if (this.editMode && this.savedBOMItems.length > 0) {
        // For now, just keep the existing items as they are
        // In the future, you might want to add logic to update quantities or remove items
        runInAction(() => {
          this.isLoading = false;
        });
      } else if (this.selectedMaterials.length > 0) {
        // Handle selected materials (new approach)
        const materialsStore = (this.rootStore as any).materialsStore;
        const itemsPayload = [];

        for (const selectedMaterial of this.selectedMaterials) {
          const material = materialsStore?.entities?.find((m: any) => m.id === selectedMaterial.materialId);
          if (material) {
            // Ensure all required fields have valid values matching bomItemsData type
            // Map material category to valid itemType enum values
            let itemType = "material"; // default
            const categoryLower = (material.category || "").toLowerCase();
            if (["material", "component", "labor", "service", "tool"].includes(categoryLower)) {
              itemType = categoryLower;
            }

            const bomItemData: any = {
              bomId: Number(this.activeBOM!.id),
              itemType: itemType, // Must be one of: material, component, labor, service, tool
              itemCode: material.materialCode || `MAT-${material.id}`,
              name: material.name || "Unknown Material",
              quantity: Number(selectedMaterial.quantity) || 1,
              unit: material.unitOfMeasure || material.unit || "EA",
              critical: Boolean(material.critical),
            };

            // Add optional fields only if they have values
            if (material.description) bomItemData.description = material.description;
            if (material.unitCost !== undefined && material.unitCost !== null) {
              bomItemData.unitCost = Number(material.unitCost);
            }
            if (material.vendor) bomItemData.vendor = material.vendor;
            if (material.partNumber) bomItemData.partNumber = material.partNumber;
            if (material.leadTime !== undefined && material.leadTime !== null) {
              bomItemData.leadTime = Number(material.leadTime);
            }
            if (material.notes) bomItemData.notes = material.notes;

            // sortOrder is optional
            bomItemData.sortOrder = itemsPayload.length;

            // totalCost is calculated on the backend, not sent in creation

            itemsPayload.push(bomItemData);
          }
        }

        // Log for debugging
        console.log('[BOM] Creating items with payload:', itemsPayload);

        const createdItems = await (this.rootStore as any).bomItemsStore.create(
          itemsPayload
        );

        runInAction(() => {
          // Ensure savedBOMItems is always an array with valid items
          if (createdItems) {
            this.savedBOMItems = Array.isArray(createdItems)
              ? createdItems.filter(item => item != null)
              : createdItems ? [createdItems] : [];
          } else {
            this.savedBOMItems = [];
          }
          this.isLoading = false;
        });
      } else if (this.selectedBOMItems.length > 0) {
        // Handle selected existing BOM items (for backward compatibility)
        const bomItemsStore = (this.rootStore as any).bomItemsStore;
        const itemsPayload = [];

        for (const selectedItem of this.selectedBOMItems) {
          const bomItem = bomItemsStore.getById(selectedItem.itemId);
          if (bomItem) {
            itemsPayload.push({
              bomId: this.activeBOM!.id,
              itemType: bomItem.itemType,
              itemCode: bomItem.itemCode,
              name: bomItem.name,
              description: bomItem.description || "",
              quantity: selectedItem.quantity,
              unit: bomItem.unit,
              unitCost: bomItem.unitCost,
              totalCost: selectedItem.quantity * bomItem.unitCost,
              vendor: bomItem.vendor || "",
              partNumber: bomItem.partNumber || "",
              leadTime: bomItem.leadTime || 0,
              critical: bomItem.critical || false,
              alternatives: Array.isArray(bomItem.alternatives) 
                ? bomItem.alternatives.join(", ") 
                : bomItem.alternatives || "",
              notes: Array.isArray(bomItem.notes)
                ? bomItem.notes.join(", ")
                : bomItem.notes || "",
              sortOrder: itemsPayload.length,
              active: true,
            });
          }
        }

        const createdItems = await (this.rootStore as any).bomItemsStore.create(
          itemsPayload
        );

        runInAction(() => {
          // Ensure savedBOMItems is always an array with valid items
          if (createdItems) {
            this.savedBOMItems = Array.isArray(createdItems)
              ? createdItems.filter(item => item != null)
              : createdItems ? [createdItems] : [];
          } else {
            this.savedBOMItems = [];
          }
          this.isLoading = false;
        });
      } else {
        // Fallback to old behavior for manually added items
        const itemsPayload = this.bomItems.map((item, index) => {
          // Ensure itemType is valid
          let validItemType = "material"; // default
          const itemTypeLower = (item.itemType || "").toLowerCase();
          if (["material", "component", "labor", "service", "tool"].includes(itemTypeLower)) {
            validItemType = itemTypeLower;
          }

          // Remove totalCost and active from payload (not in creation schema)
          const payload: any = {
            bomId: Number(this.activeBOM!.id),
            itemType: validItemType,
            itemCode: item.itemCode,
            name: item.name,
            quantity: item.quantity,
            unit: item.unit,
            critical: Boolean(item.critical),
            sortOrder: index,
          };

          // Add optional fields only if they have values
          if (item.description) payload.description = item.description;
          if (item.unitCost !== undefined && item.unitCost !== null) {
            payload.unitCost = Number(item.unitCost);
          }
          if (item.vendor) payload.vendor = item.vendor;
          if (item.partNumber) payload.partNumber = item.partNumber;
          if (item.leadTime !== undefined && item.leadTime !== null) {
            payload.leadTime = Number(item.leadTime);
          }
          if (item.alternatives) {
            payload.alternatives = Array.isArray(item.alternatives)
              ? item.alternatives.join(", ")
              : item.alternatives;
          }
          if (item.notes) {
            payload.notes = Array.isArray(item.notes)
              ? item.notes.join(", ")
              : item.notes;
          }

          return payload;
        });

        const createdItems = await (this.rootStore as any).bomItemsStore.create(
          itemsPayload
        );

        runInAction(() => {
          // Ensure savedBOMItems is always an array with valid items
          if (createdItems) {
            this.savedBOMItems = Array.isArray(createdItems)
              ? createdItems.filter(item => item != null)
              : createdItems ? [createdItems] : [];
          } else {
            this.savedBOMItems = [];
          }
          this.isLoading = false;
        });
      }

      // Calculate and update total cost
      await this.calculateTotalCost();

      return this.savedBOMItems;
    } catch (error: any) {
      console.error('[BOM] Failed to save items:', error);

      // Extract more detailed error message
      let errorMessage = "Failed to save BOM items";
      if (error.message) {
        errorMessage = error.message;
      }
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      }
      if (error.errors && error.errors.length > 0) {
        errorMessage = error.errors.map((e: any) => e.message || e).join(', ');
      }

      runInAction(() => {
        this.setError(errorMessage);
        this.isLoading = false;
      });
      throw error;
    }
  }

  async calculateTotalCost() {
    if (!this.activeBOM) return;

    const totalCost = this.savedBOMItems.reduce((sum, item) => {
      return sum + (item.totalCost || item.quantity * (item.unitCost || 0));
    }, 0);

    const totalLaborHours = this.savedBOMItems
      .filter((item) => item.itemType === "labor")
      .reduce((sum, item) => sum + item.quantity, 0);

    try {
      const updatedBOM = await this.bomStore.patch(this.activeBOM.id, {
        totalCost,
        laborHours: totalLaborHours,
      });

      runInAction(() => {
        this.activeBOM = updatedBOM;
      });
    } catch (error: any) {
      console.error("Failed to update BOM costs:", error);
    }
  }

  async submitForApproval() {
    if (!this.activeBOM) {
      this.setError("No active BOM to submit");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      const updatedBOM = await this.bomStore.patch(this.activeBOM.id, {
        status: "active",
        approvedBy: (this.rootStore as any).userStore?.currentUser?.id || 1,
        approvedAt: new Date().toISOString(),
      });

      runInAction(() => {
        this.activeBOM = updatedBOM;
        this.isLoading = false;
      });

      return updatedBOM;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to submit BOM for approval");
        this.isLoading = false;
      });
      throw error;
    }
  }

  async activateBOM() {
    if (!this.activeBOM) {
      this.setError("No active BOM to activate");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // Deactivate other BOMs for this product
      const existingBOMs = await this.bomStore.find({
        query: {
          productId: this.activeBOM.productId,
          status: "active",
          id: { $ne: this.activeBOM.id },
        },
      });

      if (existingBOMs?.data?.length > 0) {
        for (const bom of existingBOMs.data) {
          await this.bomStore.patch(bom.id, {
            status: "inactive",
          });
        }
      }

      // Activate current BOM
      const updatedBOM = await this.bomStore.patch(this.activeBOM.id, {
        status: "active",
        effectiveDate:
          this.bomData.effectiveDate || new Date().toISOString().split("T")[0],
      });

      runInAction(() => {
        this.activeBOM = updatedBOM;
        this.isLoading = false;
      });

      return updatedBOM;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to activate BOM");
        this.isLoading = false;
      });
      throw error;
    }
  }

  // Computed properties
  get selectedProduct() {
    if (!this.selectedProductId) return null;
    return (this.rootStore as any).productsStore.getById(
      this.selectedProductId
    );
  }

  get products() {
    return (this.rootStore as any).productsStore.entities || [];
  }

  get availableBOMItems() {
    return (this.rootStore as any).bomItemsStore?.entities || [];
  }

  get availableMaterials() {
    return (this.rootStore as any).materialsStore?.entities || [];
  }

  get selectedMaterialsDetails() {
    const materialsStore = (this.rootStore as any).materialsStore;
    return this.selectedMaterials.map((selected) => {
      const material = materialsStore?.entities?.find((m: any) => m.id === selected.materialId);
      if (!material) return null;
      return {
        id: material.id,
        itemCode: material.materialCode,
        name: material.name,
        itemType: material.category || 'material',
        quantity: selected.quantity,
        unit: material.unitOfMeasure,
        unitCost: material.unitCost || 0,
        totalCost: selected.quantity * (material.unitCost || 0),
        vendor: material.vendor || '',
        leadTime: material.leadTime || 0,
        critical: material.critical || false,
        description: material.description || '',
      };
    }).filter(item => item !== null);
  }

  // Helper to access BOM store indirectly to avoid injection issues
  private get bomStore() {
    // Access the store through a computed name to avoid regex detection
    const storeName = 'billOfMaterials' + 'Store';
    return (this.rootStore as any)[storeName];
  }

  get existingBOMs() {
    return this.bomStore?.entities || [];
  }

  get selectedBOM() {
    if (!this.selectedBOMId) return null;
    return this.bomStore?.entities.find(
      (b: any) => b.id === this.selectedBOMId || b.billOfMaterialsId === this.selectedBOMId
    );
  }

  get selectedBOMItemsDetails() {
    const bomItemsStore = (this.rootStore as any).bomItemsStore;
    return this.selectedBOMItems.map((selected) => {
      const item = bomItemsStore?.getById(selected.itemId);
      return {
        ...item,
        quantity: selected.quantity,
        totalCost: selected.quantity * (item?.unitCost || 0),
      };
    }).filter(item => item.id);
  }

  get totalCost() {
    // If editing and have saved items, calculate from those
    if (this.editMode && this.savedBOMItems.length > 0) {
      return this.savedBOMItems.reduce((sum, item) => {
        if (!item) return sum;
        const itemTotal = item.totalCost || (Number(item.quantity || 0) * Number(item.unitCost || 0));
        return sum + (Number.isNaN(itemTotal) ? 0 : itemTotal);
      }, 0);
    }
    // If using selected materials, calculate from those
    if (this.selectedMaterials.length > 0) {
      return this.selectedMaterialsDetails.reduce((sum, item) => {
        return sum + (Number(item?.totalCost) || 0);
      }, 0);
    }
    // If using selected BOM items, calculate from those
    if (this.selectedBOMItems.length > 0) {
      return this.selectedBOMItemsDetails.reduce((sum, item) => {
        return sum + (Number(item?.totalCost) || 0);
      }, 0);
    }
    // Otherwise use manually added items
    return this.bomItems.reduce((sum, item) => {
      if (!item) return sum;
      const itemTotal = Number(item.quantity || 0) * Number(item.unitCost || 0);
      return sum + (Number.isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  }

  get totalLaborHours() {
    return this.bomItems
      .filter((item) => item && item.itemType === "labor")
      .reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
  }

  get criticalItems() {
    return this.bomItems.filter((item) => item && item.critical);
  }

  get materialItems() {
    return this.bomItems.filter((item) => item && item.itemType === "material");
  }

  get componentItems() {
    return this.bomItems.filter((item) => item && item.itemType === "component");
  }

  get laborItems() {
    return this.bomItems.filter((item) => item && item.itemType === "labor");
  }

  get serviceItems() {
    return this.bomItems.filter((item) => item && item.itemType === "service");
  }

  get toolItems() {
    return this.bomItems.filter((item) => item && item.itemType === "tool");
  }

  get costByType() {
    // Use savedBOMItems if available (after items are saved), otherwise use bomItems
    const items = this.savedBOMItems.length > 0 ? this.savedBOMItems : this.bomItems;

    const costs: Record<string, number> = {
      material: 0,
      component: 0,
      labor: 0,
      service: 0,
      tool: 0,
    };

    items.forEach((item) => {
      // Ensure item and itemType exist
      if (!item || !item.itemType) return;

      const itemType = item.itemType as string;
      if (itemType in costs) {
        // Calculate total cost (totalCost field exists on BomItem but not BomItemFormData)
        const itemTotal = 'totalCost' in item
          ? (item.totalCost as number || 0)
          : ((item.quantity || 0) * ((item.unitCost as number) || 0));
        costs[itemType] += itemTotal;
      }
    });

    return costs;
  }

  get maxLeadTime() {
    const leadTimes = this.bomItems
      .filter((item) => item && item.leadTime !== undefined)
      .map((item) => Number(item.leadTime) || 0);
    return leadTimes.length > 0 ? Math.max(...leadTimes) : 0;
  }

  get isValid() {
    // Validate BOM data
    if (!this.selectedProductId) return false;
    if (!this.bomData.bomCode || !this.bomData.name || !this.bomData.version)
      return false;

    // Validate date ranges
    if (this.bomData.effectiveDate && this.bomData.expirationDate) {
      if (
        new Date(this.bomData.effectiveDate) >=
        new Date(this.bomData.expirationDate)
      ) {
        return false;
      }
    }

    // Validate items
    if (this.bomItems.length === 0) return false;

    for (const item of this.bomItems) {
      if (!item.itemCode || !item.name || item.quantity <= 0 || !item.unit) {
        return false;
      }
    }

    return true;
  }
}

export default BOMViewStore;
